import { createSelector } from 'reselect';
import {
  assign,
  filter,
  find,
  flow,
  get,
  identity,
  mapValues,
  path,
  reduce,
  sortBy,
  sortedUniqBy,
  values,
} from 'lodash/fp';

import * as propertyObjectsSelectors from '../../PropertyObjects/selectors';
import * as helpers from './helpers';

/**
 * @type {Function}
 */
export const getContactsContactsListSlice = createSelector(path('contacts.contactsList'), (slice) =>
  slice.toJS(),
);

/**
 * @type {Function}
 */
const getContactsSuccessorsSlice = createSelector(path('contacts.successors'), identity);

/**
 * Возвращает список Контактов:
 *  - отсортированный по коду ИФНС (в порядке увеличения)
 *  - контакт ассоциирован с правоприемником
 *
 *  @type {Function}
 */
export const getAllContactsData = createSelector(
  [
    flow(getContactsContactsListSlice, path('data')),
    flow(getContactsSuccessorsSlice, path('data')),
  ],
  (contacts, successors) => {
    return flow(
      mapValues((value) => {
        const successor = find({ taxCode: value.taxCode }, successors);
        return assign({ successor }, value);
      }),
      values,
      sortBy(['taxCode']),
    )(contacts);
  },
);

/**
 * Возвращает список всех налоговых органов
 *
 * - Все НО, связанные с объектами собственности НП
 * - Все правопреемники для неактуальных НО, связанные с объектами собственности НП
 * - Все НО, добавленные НП в список постановок на налоговый учет
 * - Все правопреемники для неактуальных НО, добавленных НП в список постановок на налоговый
 *
 * @type {Function}
 * @returns {Array<{ code: string, name: string, isActive: boolean }>}
 */
export const getTaxAuthorities = createSelector([getAllContactsData], (contacts) => {
  const reducer = reduce((accumulator, taxContact) => {
    const isActive = helpers.isTaxCodeActive(taxContact);

    accumulator.push(assign({ isActive }, helpers.evolveTax(taxContact)));

    if (!isActive && helpers.hasTaxCodeSuccessor(taxContact)) {
      accumulator.push(assign({ isActive: true }, helpers.evolveTaxSuccessor(taxContact)));
    }

    return accumulator;
  }, []);

  return flow(
    reducer,
    sortBy([(o) => (o.isActive ? 0 : 1), 'code']),
    sortedUniqBy('code'),
  )(contacts);
});

/**
 * Возвращает список всех актульных(активных) налоговых органов
 *
 * - Все актульные НО, связанные с объектами собственности НП
 * - Все правопреемники для неактуальных НО, связанные с объектами собственности НП
 * - Все актульные НО, добавленные НП в список постановок на налоговый учет
 * - Все правопреемники для неактуальных НО, добавленных НП в список постановок на налоговый
 *
 * @type {Function}
 * @returns {Array<{ code: string, name: string, isActive: true }>}
 */

export const getIsActiveTaxAuthorities = createSelector(getTaxAuthorities, filter('isActive'));

/**
 * Возвращает налоговый орган налогоплательщика
 *
 * Если у налогоплательщика есть ассоциированное с ним НО и данное НО является актуальным.
 * Иначе: Если у налогоплательщика есть ассоциированное с ним НО,
 *  которое является неактуальным – его актуальный правопреемник.
 *
 * @type {Function}
 * @returns {({ code: string, name: string } | undefined)}
 */
export const getTaxAuthorityByRegistration = createSelector([getAllContactsData], (contacts) => {
  const taxContact = helpers.findRegisteredTax(contacts);

  if (!taxContact) {
    return;
  }

  if (helpers.isTaxCodeActive(taxContact)) {
    return helpers.evolveTax(taxContact);
  } else if (helpers.hasTaxCodeSuccessor(taxContact)) {
    return helpers.evolveTaxSuccessor(taxContact);
  }
});

/**
 * Возвращает налоговый орган для объекта
 *
 * Для Транспортного средства:
 *  НО, ассоциированное с налогоплательщиком, и данное НО является актуальным.
 *  Иначе: Если у налогоплательщика есть ассоциированное с ним НО,
 *    которое является неактуальным – его актуальный правопреемник
 *
 * Для Земельного участка и Недвижимости:
 *  ИФНС, ассоциированная с данным объектом налогообложения и данное НО является актуальным.
 *  Иначе: Если у объекта есть ассоциированное с ним НО,
 *    которое является неактуальным – его актуальный правопреемник
 *
 * @comment Если в "Контактах" не нашелся `objectTaxContact`, "пинаем" Базу/Бэк - сценарий невозможен,
 *  по причине, что контакты строятся на основе объектов, с привязанными к ним ИФНС
 *
 * @type {Function}
 * @returns {({ code: string, name: string } | undefined)}
 */
export const getTaxAuthorityForObject = createSelector(
  [
    (state, ownProps) => ownProps.selectedObjectId,
    propertyObjectsSelectors.getPropertyObjectsData,
    getAllContactsData,
    getTaxAuthorityByRegistration,
  ],
  (selectedObjectId, propertyObjects, contacts, defaultTaxAuthority) => {
    if (!selectedObjectId) return;

    const selectedObject = get(selectedObjectId, propertyObjects);

    if (selectedObject && selectedObject.isTransport()) {
      return defaultTaxAuthority;
    }

    const objectTaxCode = helpers.getTaxAuthorityCode(selectedObject);
    const objectTaxContact = find({ taxCode: objectTaxCode }, contacts);

    if (helpers.isTaxCodeActive(objectTaxContact)) {
      return helpers.evolveTax(objectTaxContact);
    } else if (helpers.hasTaxCodeSuccessor(objectTaxContact)) {
      return helpers.evolveTaxSuccessor(objectTaxContact);
    }
  },
);

export const makeGetTaxAuthorityForObject = () => {
  return createSelector(
    [
      (state, ownProps) => ownProps.selectedObjectId,
      propertyObjectsSelectors.getPropertyObjectsData,
      getAllContactsData,
      getTaxAuthorityByRegistration,
    ],
    (selectedObjectId, propertyObjects, contacts, defaultTaxAuthority) => {
      if (!selectedObjectId) return;

      const selectedObject = get(selectedObjectId, propertyObjects);

      if (selectedObject.isTransport()) {
        return defaultTaxAuthority;
      }

      const objectTaxCode = helpers.getTaxAuthorityCode(selectedObject);
      const objectTaxContact = find({ taxCode: objectTaxCode }, contacts);

      if (helpers.isTaxCodeActive(objectTaxContact)) {
        return helpers.evolveTax(objectTaxContact);
      } else if (helpers.hasTaxCodeSuccessor(objectTaxContact)) {
        return helpers.evolveTaxSuccessor(objectTaxContact);
      }
    },
  );
};
